import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Popup, Button, SelectBox, RadioGroup } from 'devextreme-react';
import '../../css/omstyle.css'
import SearchResultsForDialog, {
    DIFFERENCE_SAME_ITEM_NEW_LOT,
    DIFFERENCE_DO_NOTHING
} from './SearchResultsForDialog';

import Toolbar, { Item } from "devextreme-react/toolbar";

import { connect } from "react-redux";
import AccordionReact from 'devextreme-react/accordion';
import routes from "../../../assets/apiRoutes.json";
import axios from "axios";
import DataSource from "devextreme/data/data_source";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { alert } from 'devextreme/ui/dialog';
import { MESSAGES } from "../../../common/Messages";

const searchpanel = [
    {
        ID: 1,
        Title: 'Search Filters'
    }
];

// Captions and order are Delphi's grpActionSelection Items.Strings (uOMD0022 DFM), ItemIndex 0-2 as id 1-3.
// The captions have to match the DFM exactly: the exit guard below quotes them back at the user.
const priorityEntities = [
    { id: DIFFERENCE_SAME_ITEM_NEW_LOT, text: 'Add Same Item With Different Lot' },
    { id: 2, text: 'Add Different Item/Lot' },
    { id: DIFFERENCE_DO_NOTHING, text: 'Do Nothing / Other' },
]

// Delphi's AbColumnLabel1, the heading above the radio group.
const DIFFERENCE_HANDLING_PROMPT = 'How Do You Wish To Handle The Difference?';

const formatNumber = (value) =>
    value === null || value === undefined || value === ""
        ? ""
        : Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatCurrency = (value) =>
    value === null || value === undefined || value === ""
        ? ""
        : Number(value).toLocaleString(undefined, { style: "currency", currency: "USD", minimumFractionDigits: 4 });

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "");

/**
 * One read-only cell of the item-statistics strip (Delphi's AbGroupBox1 labels + disabled edits).
 *
 * Deliberately not DevExtreme's .dx-field / .dx-field-label: those put the label in a fixed-width
 * column with text-overflow: ellipsis, which clipped "Net Available", "Qty Difference",
 * "Transaction #" and "Order Date" to "Net Avail...". The label sits above its value instead, so
 * each one gets the full width of its cell.
 */
const InfoField = ({ label, value }) => (
    <div className="allocate-difference-popup__stat">
        <span className="allocate-difference-popup__stat-label">{label}</span>
        <span className="allocate-difference-popup__stat-value">{value || "—"}</span>
    </div>
);

const AllocateDialog = (props) => {
    var accessToken = props.oidc.user.access_token;
    const [searchPanelStatus, setSearchPanelStatus] = useState(true);
    const [selectedItems, setSelectedItems] = useState([searchpanel[0]]);
    const [transactionHeaderId, setTransactionHeaderId] = useState(null);
    const [differenceInfo, setDifferenceInfo] = useState(null);
    const [sessionRowCount, setSessionRowCount] = useState(0);

    // Delphi's fmDifference vs fmAdd: the caller passes a detail row only when there is a difference to
    // resolve. It drives the title, whether the header is locked, and whether the radio group and the
    // item-statistics strip are shown at all (SetMode).
    const isDifferenceMode = !!props.data?.detailRowId;

    const [selectionPriority, setSelectionPriority] = useState(
        isDifferenceMode ? priorityEntities[0] : priorityEntities[2]);

    const selectionChanged = useCallback(
        (e, tg) => {
            let newItems = [...selectedItems];
            e.removedItems.forEach((item) => {
                const index = newItems.indexOf(item);
                if (index >= 0) {
                    newItems.splice(index, 1);
                }
            });
            if (e.addedItems.length) {
                newItems = [...newItems, ...e.addedItems];
            }
            setSelectedItems(newItems);
            setSearchPanelStatus((prevStatus) => {
                const newStatus = !prevStatus;
                return newStatus;
            });
        },
        [selectedItems, setSelectedItems],
    );

    const selectBoxData = useMemo(() => {
        return new DataSource({
            store: createStore({
                loadUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.GetTransactionsHeader}`,
                key: 'rowid',
                onBeforeSend: function (method, ajaxOptions) {
                    ajaxOptions.headers = {
                        Authorization: `Bearer ${accessToken}`
                    };
                }
            }),
            paginate: true,
            pageSize: 10
        });
    }, [accessToken]);

    const changeSelectionPriority = useCallback(
        (e) => {
            setSelectionPriority(e.value);
        },
        [setSelectionPriority],
    );

    // In difference mode the header is the one the caller passed in; in add mode it is whatever the user
    // picks from the transaction dropdown.
    const activeHeaderRowId = props.data?.headerRowId ?? transactionHeaderId;

    // Delphi's SetItemLotInfo plus the TransDetailGroupBax bindings. A snapshot, not re-read as rows are
    // edited — but in add mode it does follow the transaction dropdown, which is Delphi's
    // qeTransactionHeaderAfterScroll re-pointing the header query.
    useEffect(() => {
        const itemRowId = isDifferenceMode ? props.data?.itemid : null;
        if (!itemRowId && !activeHeaderRowId) {
            setDifferenceInfo(null);
            return;
        }

        const params = new URLSearchParams();
        if (itemRowId) params.append("itemRowId", itemRowId);
        if (isDifferenceMode && props.data?.lotRowId) params.append("lotRowId", props.data.lotRowId);
        if (activeHeaderRowId) params.append("headerRowId", activeHeaderRowId);

        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetDifferenceInfo}?${params.toString()}`,
                { headers: { Authorization: `Bearer ${accessToken}`, access_token: accessToken } })
            .then((res) => setDifferenceInfo(res.data))
            .catch(() => setDifferenceInfo(null));
    }, [isDifferenceMode, props.data?.itemid, props.data?.lotRowId, activeHeaderRowId, accessToken]);

    // Qty Difference is the caller's own number, as in Delphi (the DiffQty property OMD0021 sets).
    const qtyDifference = useMemo(() => {
        const ordered = props.data?.quantityOrdered;
        const newOrdered = props.data?.newquantityorder;
        if (ordered === null || ordered === undefined || newOrdered === null || newOrdered === undefined) return null;
        return Number(ordered) - Number(newOrdered);
    }, [props.data?.quantityOrdered, props.data?.newquantityorder]);

    const close = useCallback(() => props.setOpenAllocateDialog(false), [props]);

    /**
     * Delphi's exit guard (navTransactionHeaderButtonClick(abExit)): choosing one of the two "Add" options
     * and then adding nothing is refused. Only the count differs — Delphi counted the ProcessFlag='Y' rows
     * staged in the session, and with no staging on the web the equivalent is the rows added in this dialog.
     */
    const canClose = useCallback(() => {
        const needsDetail = isDifferenceMode && selectionPriority?.id !== DIFFERENCE_DO_NOTHING;
        if (needsDetail && sessionRowCount === 0) {
            alert(
                MESSAGES.DIFFERENCE_HANDLING_REQUIRES_DETAILS
                    .replace("{0}", selectionPriority?.text ?? "")
                    .replace("{1}", priorityEntities[2].text),
                MESSAGES.ERROR);
            return false;
        }
        return true;
    }, [isDifferenceMode, selectionPriority, sessionRowCount]);

    const handleClose = useCallback(() => {
        if (canClose()) close();
    }, [canClose, close]);

    // The popup's X and Escape close unconditionally — no guard, no prompt. The exit guard runs on the
    // OK button only. Delphi's FormClose did veto here, but a close box that argues back is not the
    // behaviour wanted on the web.
    const handleHiding = useCallback(() => close(), [close]);

    /**
     * Delphi's &Abort (btnAbortClick -> CloseDlg) closes with no confirmation prompt and skips the exit
     * guard, and so does this.
     *
     * It cannot do Delphi's other half — CloseDlg also ran DeleteTempDetails, because every row was only
     * ever a ProcessFlag='Y' staging row, whereas the web inserts detail lines permanently. Delphi's own
     * answer to that is qeTransactionDetailAfterScroll, which *disables* Abort as soon as a row exists
     * (`btnAbort.Enabled := (not RecordsExist) or ...`), so "abort" never has anything to undo. Reproducing
     * that is what keeps the button honest here: once a line has been added it must be removed from the
     * grid's own delete, not silently abandoned by a button that claims to discard it.
     */
    const handleAbort = useCallback(() => close(), [close]);
    const canAbort = sessionRowCount === 0;

    return (
        <div>
            <Popup
                visible={props.openAllocateDialog}
                onHiding={handleHiding}
                showCloseButton={true}
                title={isDifferenceMode ? "Handle Quantity Difference" : "Insert Detail Lines"}
                // An explicit height is what gives the flex column something to divide up; with the
                // default auto height the grid row has no remaining space to claim.
                height="90vh"
                wrapperAttr={{ class: "allocate-difference-popup" }}
            >
                {/* The flex column lives on an element this component owns, so the layout does not depend
                    on wrapperAttr reaching .dx-popup-content. */}
                <div className="allocate-difference-popup__content">
                <div className="row">
                    <div className="col-md-12 main-page">
                        <div id="accordion" >
                            <AccordionReact
                                dataSource={searchpanel}
                                collapsible={true}
                                multiple={false}
                                animationDuration={300}
                                selectedItems={selectedItems}
                                onSelectionChanged={e => {
                                    if (e.currentTarget === e.target) {
                                        selectionChanged(e, e.target);
                                    }
                                }}
                                itemTitleRender={() => (
                                    <Toolbar className="accordian-toolbar">

                                    </Toolbar>

                                )}
                                itemRender={(itemProps) => (
                                    <div>
                                        <form>
                                            <div className="row">
                                                <div className="col-md-3">

                                                    <SelectBox
                                                        stylingMode='outlined'
                                                        labelMode="static"
                                                        label="Transaction #"
                                                        defaultValue={props.data?.headerRowId}
                                                        dataSource={selectBoxData}
                                                        valueExpr='rowid'
                                                        displayExpr='code'
                                                        acceptCustomValue={true}
                                                        searchEnabled={true}
                                                        searchExpr="Code"
                                                        searchMode="contains"
                                                        onValueChanged={(e) => {
                                                            setTransactionHeaderId(e.value);
                                                        }}
                                                        // Delphi disables abFind in fmDifference: the dialog
                                                        // is locked to the transaction the caller passed in.
                                                        disabled={isDifferenceMode}
                                                    />
                                                </div>
                                                {isDifferenceMode && (
                                                    <div className="col-md-9">
                                                        {/* Delphi's AbColumnLabel1 caption above grpActionSelection. */}
                                                        <div className="allocate-difference-popup__stat-label">
                                                            {DIFFERENCE_HANDLING_PROMPT}
                                                        </div>
                                                        <RadioGroup
                                                            items={priorityEntities}
                                                            value={selectionPriority}
                                                            onValueChanged={changeSelectionPriority}
                                                            layout="horizontal"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </form>

                                    </div>
                                )}
                                id="accordion-container" className="accordion-height"
                            />
                        </div>
                    </div>
                </div>

                {/* Read-only, populated from the header / item snapshot above.

                    Two Delphi group boxes, and SetMode toggles only one of them: AbGroupBox1 (WH … Price) is
                    fmDifference-only, while TransDetailGroupBax (Customer … Record #) is never touched by
                    SetMode and so stays on screen in Insert Detail Lines mode too. */}
                <div className="allocate-difference-popup__stats">
                    {isDifferenceMode && (
                        <>
                            <InfoField label="WH" value={differenceInfo?.warehouseIdentityId} />
                            <InfoField label="Item" value={differenceInfo?.itemCode} />
                            <InfoField label="Lot" value={differenceInfo?.lotCode} />
                            <InfoField label="Net Available" value={formatNumber(differenceInfo?.netAvailable)} />
                            <InfoField label="Qty Difference" value={formatNumber(qtyDifference)} />
                            <InfoField label="Price" value={formatCurrency(props.data?.unitPrice)} />
                        </>
                    )}
                    <InfoField label="Customer" value={differenceInfo?.customerIdentityId} />
                    <InfoField label="Transaction #" value={differenceInfo?.transactionNumber} />
                    <InfoField label="Order Date" value={formatDate(differenceInfo?.transactionDate)} />
                    <InfoField label="Record #" value={differenceInfo?.recordNumber} />
                </div>

                <SearchResultsForDialog
                    data={props.data}
                    accessToken={accessToken}
                    headerRowId={transactionHeaderId}
                    priorityEntities={selectionPriority}
                    differenceQuantity={qtyDifference}
                    onSessionRowCountChange={setSessionRowCount}
                />

                <Toolbar>
                    <Item location="after">
                        <Button text="OK" type="default" onClick={handleClose} />
                    </Item>
                    {isDifferenceMode && (
                        <Item location="after">
                            <Button text="Abort" onClick={handleAbort} disabled={!canAbort} />
                        </Item>
                    )}
                </Toolbar>
                </div>
            </Popup>
        </div>
    )
}
function mapStateToProps(state) {
    return {
        ReceiptReducer: state.ReceiptReducer,
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(AllocateDialog);
